import { motion } from "motion/react";
import { cn } from "./utils";

export type LoaderStyle = "spinner" | "logo" | "progress";

export interface LoaderProps {
  /** Visual treatment — matches the embed "loading screen" options. */
  variant?: LoaderStyle;
  /** Logo data URL / src for the `logo` variant (falls back to spinner). */
  logo?: string;
  /** Tailwind size for the spinner / logo (px). */
  size?: number;
  /** Tone of the spinner ring + bar. */
  tone?: "gold" | "ink" | "white";
  label?: string;
  className?: string;
}

const TONE: Record<NonNullable<LoaderProps["tone"]>, { ring: string; bar: string }> = {
  gold: { ring: "border-gold-400", bar: "bg-gold-400" },
  ink: { ring: "border-ink-700", bar: "bg-ink-700" },
  white: { ring: "border-white", bar: "bg-white" },
};

/** Loading indicator with the three embed loader styles (spinner / logo / progress). */
export function Loader({ variant = "spinner", logo, size = 32, tone = "gold", label, className }: LoaderProps) {
  const t = TONE[tone];

  if (variant === "logo" && logo) {
    return (
      <div className={cn("flex flex-col items-center gap-3", className)}>
        <motion.img
          src={logo}
          alt=""
          style={{ width: size * 2, height: size * 2 }}
          className="object-contain"
          initial={{ opacity: 0.4, scale: 0.96 }}
          animate={{ opacity: [0.4, 1, 0.4], scale: [0.96, 1, 0.96] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
        {label && <p className="text-[12px] font-medium text-ink-400">{label}</p>}
      </div>
    );
  }

  if (variant === "progress") {
    return (
      <div className={cn("flex w-44 flex-col items-center gap-3", className)}>
        <div className="h-1 w-full overflow-hidden rounded-full bg-ink-200/40">
          <motion.div
            className={cn("h-full w-1/3 rounded-full", t.bar)}
            animate={{ x: ["-100%", "300%"] }}
            transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        {label && <p className="text-[12px] font-medium text-ink-400">{label}</p>}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <div
        style={{ width: size, height: size }}
        className={cn("animate-spin rounded-full border-2 border-t-transparent", t.ring)}
      />
      {label && <p className="text-[12px] font-medium text-ink-400">{label}</p>}
    </div>
  );
}
